import { useEffect, useCallback, useMemo } from 'react'

import { useParams } from 'react-router-dom'

import { makeIsMetaAdsCampaignClosed } from 'src/Redux/typeform-process/selectors'
import { useGetMetaAdsCampaignDetailsQuery } from 'src/Redux/metaAdsDetailsApi'
import { useAppSelector, useAppDispatch } from 'src/Hooks/redux'
import { getUserEmail } from 'src/Redux/auth-process/userSlice/selectors'
import { setClosedMetaAdsTypeformList } from 'src/Redux/typeform-process'

declare global {
  interface Window {
    tf?: {
      createPopup: (
        formId: string,
        options?: {
          hidden?: Record<string, string>
          onSubmit?: () => void
          onClose?: () => void
        },
      ) => { open: () => void }
    }
  }
}

const TYPEFORM_ID = 'jXtzUasi'

export const useTypeformForMetaAds = (): React.ReactNode => {
  const dispatch = useAppDispatch()
  const { campaignID } = useParams<{ campaignID: string }>()
  const userEmail = useAppSelector(getUserEmail)
  const { data } = useGetMetaAdsCampaignDetailsQuery(campaignID || '', {
    skip: !campaignID,
  })

  const isAppropriateCampaignStatus = data?.status && data?.status === 'ended'

  const isClosedSelector = useMemo(
    () => makeIsMetaAdsCampaignClosed(String(campaignID)),
    [campaignID],
  )

  const isClosedCampaign = useAppSelector(isClosedSelector)

  const markClosed = useCallback(() => {
    if (campaignID) {
      dispatch(setClosedMetaAdsTypeformList(String(campaignID)))
    }
  }, [campaignID, dispatch])

  useEffect(() => {
    const shouldRun = isAppropriateCampaignStatus && !isClosedCampaign

    if (shouldRun) {
      const scriptId = 'typeform-embed-script'
      let script = document.getElementById(scriptId) as HTMLScriptElement | null
      let scriptAppended = false
      if (!script) {
        script = document.createElement('script')
        script.id = scriptId
        script.src = 'https://embed.typeform.com/next/embed.js'
        script.async = true
        document.body.appendChild(script)
        scriptAppended = true
      }
      if (script) {
        script.onload = () => {
          if (window.tf?.createPopup) {
            const popup = window.tf.createPopup(TYPEFORM_ID, {
              hidden: {
                email: userEmail,
                campaign_id: data?.id?.toString() || '',
                campaign_type: 'meta-ads',
                campaign_name: data?.resourceName || '',
                campaign_strategy: data?.strategy || '',
              },
              onSubmit: markClosed,
              onClose: markClosed,
            })
            popup.open()
          }
        }
      }

      return () => {
        if (scriptAppended && script && document.body.contains(script)) {
          document.body.removeChild(script)
        }
      }
    }
    return void 0
  }, [
    data?.id,
    data?.resourceName,
    data?.strategy,
    isAppropriateCampaignStatus,
    isClosedCampaign,
    markClosed,
    userEmail,
  ])

  return null
}
